import { BodyFatFormula } from "./body-fat-formula";

function BodyFatContents() {
  return (
    <nav aria-label="Rehber içindekiler" className="not-prose static">
      <h2 className="mb-6 font-bold text-2xl">İçindekiler</h2>
      <ol className="space-y-4 text-muted-foreground text-sm">
        {[
          ["yag-orani-nedir", "Yağ oranı nedir?"],
          ["yontem-secimi", "Navy ve diğer yöntemler"],
          ["mezura-olcumu", "Doğru ölçüm nasıl alınır?"],
          ["formuller", "Formüller ve örnekler"],
          ["sonucu-yorumlama", "Sonucunu yorumla"],
          ["arastirmalar", "Türkiye'deki araştırmalar"],
          ["sss", "Sık sorulan sorular"],
        ].map(([id, label], index) => (
          <li key={id}>
            <a
              href={`#${id}`}
              className="group flex items-center gap-3 py-1 hover:text-foreground"
            >
              <span className="text-primary text-sm">0{index + 1}</span>
              {label}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function BodyFatGuide() {
  return (
    <section className="grid items-start py-12 xl:grid-cols-[minmax(0,1fr)_40rem_minmax(0,1fr)] xl:gap-8">
      <aside className="hidden xl:block">
        <BodyFatContents />
      </aside>
      <article
        lang="tr"
        aria-label="Vücut yağ oranı rehberi"
        className="prose prose-zinc dark:prose-invert mx-auto prose-h2:mt-14 w-full min-w-0 max-w-[40rem] prose-headings:scroll-mt-28 prose-a:text-primary prose-h2:text-2xl prose-h3:text-lg prose-p:leading-relaxed xl:col-start-2"
      >
        <p className="text-muted-foreground text-sm">
          AtalayTheCoach · Ölçüm ve yöntem rehberi · Güncelleme:{" "}
          <time dateTime="2026-09-28">28 Eylül 2026</time>
        </p>
        <h2>Yağ oranını ölçmek ve sonucu anlamak</h2>
        <div className="not-prose my-8 xl:hidden">
          <BodyFatContents />
        </div>
        <p>
          Tartıda gördüğün kilo, vücudundaki yağ miktarını tek başına anlatmaz.
          Aynı kilodaki iki kişinin kas kütlesi ve vücut yapısı farklı olabilir.
          Vücut yağ oranı hesaplama, toplam ağırlığının ne kadarının yağdan
          oluştuğunu tahmin ederek bu resmi tamamlamana yardımcı olur. Ancak
          çıkan yüzdeyi doğru yorumlamak için hangi yöntemi kullandığını ve
          ölçülerini nasıl aldığını bilmen gerekir.
        </p>
        <p>
          <a href="#hesaplayici">
            AtalayTheCoach vücut yağ oranı hesaplayıcısında
          </a>{" "}
          Navy yöntemi kullanılır. Bu rehber, mezurayla doğru ölçüm almanı,
          ölçüm hatalarını azaltmanı ve sonucu gelişim takibinde kullanmanı
          sağlayacak.
        </p>
        <p>
          <strong>Hızlı başlangıç:</strong> Boyunu, boyun ve bel ya da karın
          çevreni ölç. Kadınlar için kalça çevresini de ekle. Tüm uzunlukları
          santimetre olarak gir. Kilo yalnızca yağ ve yağsız kütleyi kilogram
          olarak hesaplamak için isteğe bağlıdır. Sonuç kişisel sağlık
          değerlendirmesinin yerine geçmez.
        </p>
        <h2 id="yag-orani-nedir">Vücut yağ oranı nedir, kilodan farkı ne?</h2>
        <p>
          Vücut yağ oranı, yağ kütlesinin toplam vücut ağırlığı içindeki yüzde
          payıdır. Örneğin 80 kg ağırlığındaki bir kişinin yağ oranı %20 ise
          tahmini yağ kütlesi 16 kg, yağsız kütlesi 64 kg olur.
        </p>
        <p>Hesap şu şekilde yapılır:</p>
        <ul>
          <li>
            <strong>Yağ kütlesi:</strong> Vücut ağırlığı × yağ oranı / 100
          </li>
          <li>
            <strong>Yağsız kütle:</strong> Vücut ağırlığı − yağ kütlesi
          </li>
        </ul>
        <p>
          Buradaki 64 kg, kişinin 64 kg kası olduğu anlamına gelmez. Yağsız
          kütle; kasların yanında suyu, kemikleri, organları ve diğer yağ dışı
          bileşenleri de içerir. Ayrıca başlangıçtaki yağ yüzdesi tahminiyse,
          ondan hesaplanan kilogram değerleri de tahminidir.
        </p>
        <h3>Yağ oranı ile vücut kitle indeksi aynı şey mi?</h3>
        <p>
          Hayır. Vücut kitle indeksi, yani BMI veya BKİ, kilonun metre cinsinden
          boyun karesine bölünmesiyle bulunur. Kas ile yağı ayıramaz. Bu nedenle
          kas kütlesi yüksek bir kişinin BMI değeri yüksek çıkabilir; bu sonuç
          tek başına yağ oranını göstermez.{" "}
          <a href="https://www.cdc.gov/bmi/faq/">CDC: BMI hakkında sorular</a>
        </p>
        <p>
          Yağ oranını öğrenmenin değeri de burada ortaya çıkar: ağırlık
          değişimini daha iyi yorumlamana yardımcı olur. Yine de tek bir yüzde;
          gücünü, kondisyonunu, beslenme yeterliliğini veya bütün sağlık
          durumunu açıklamaz.
        </p>
        <h2 id="yontem-secimi">
          Navy yöntemi diğer yöntemlerden nasıl ayrılır?
        </h2>
        <p>
          Bu hesaplayıcı yalnızca Navy yöntemini kullanır. Aşağıdaki
          karşılaştırma, kaliper veya BMI gibi farklı yöntemlerden alınan
          sonuçları anlamana yardımcı olur. Daha fazla ölçüm gerektiren bir
          yöntem, hatalı uygulandığında daha iyi sonuç vermeyebilir.
        </p>
        <div className="overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th scope="col">Yöntem</th>
                <th scope="col">Kullanılan bilgiler</th>
                <th scope="col">Seçim yaparken düşün</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Navy</td>
                <td>
                  Boy, boyun ve karın/bel çevresi; kadınlarda kalça çevresi
                </td>
                <td>Mezura ile evde takip için pratik bir seçenek</td>
              </tr>
              <tr>
                <td>Jackson–Pollock 3-Site</td>
                <td>Yaş, cinsiyet ve üç bölgeden deri kıvrımı</td>
                <td>Kaliper ve doğru bölge ölçümü gerekir</td>
              </tr>
              <tr>
                <td>Jackson–Pollock 7-Site</td>
                <td>Yaş, cinsiyet ve yedi bölgeden deri kıvrımı</td>
                <td>
                  Daha çok bölge ölçülür; yardım almak uygulamayı kolaylaştırır
                </td>
              </tr>
              <tr>
                <td>BMI / Deurenberg</td>
                <td>Boy, kilo, yaş ve cinsiyet</td>
                <td>Çevre ölçümü olmadan genel bir tahmin sağlar</td>
              </tr>
            </tbody>
          </table>
        </div>
        <h3>Navy: Mezura ile yağ oranı hesaplama</h3>
        <p>
          Bu yöntem boy ile belirli vücut çevrelerini bir denklemde birleştirir.
          Erkeklerde boyun ve karın; kadınlarda boyun, doğal bel ve kalça
          çevresi kullanılır. Ölçüm yerleri, aşağıdaki tarifle eşleşmelidir.
          Burada anlatılan çevre protokolü, Navy’nin 2023 rehberindeki yağ
          yüzdesi değerlendirme bölümüne dayanır; güncel askerî kabul şartlarını
          anlatmaz.{" "}
          <a href="https://s3.us-east-2.amazonaws.com/sidearm.nextgen.sites/usmmasports.com/documents/2025/2/24/Navy_BCA_guide.pdf">
            Navy ölçüm rehberi, Bölüm 3
          </a>
        </p>
        <h3>Jackson–Pollock: Kaliper ile yağ oranı hesaplama</h3>
        <p>
          Kaliper, belirli noktalardan tutulan deri kıvrımının kalınlığını
          ölçer. Jackson–Pollock 3 bölge yönteminde erkekler için göğüs, karın
          ve uyluk; kadınlar için triceps, uyluk ve suprailiac alanları bulunur.
          Suprailiac, leğen kemiğinin üst tarafındaki deri kıvrımı bölgesidir.
        </p>
        <p>
          Jackson–Pollock 7 bölge yönteminde göğüs, midaxillary, triceps,
          subscapular, karın, suprailiac ve uyluk ölçümleri girilir. Midaxillary
          gövdenin koltuk altı doğrultusundaki yan bölgesini, subscapular kürek
          kemiği altındaki bölgeyi ifade eder. Bu isimler tek başına ölçüm
          eğitimi yerine geçmez; noktanın ve kıvrım yönünün doğru belirlenmesi
          gerekir.
        </p>
        <p>
          Deri kıvrımı denklemleri, ölçümler ile vücut yoğunluğu arasındaki
          ilişkiden geliştirilmiştir. Sonuçları değerlendirirken denklemin
          geliştirildiği yaş grubu ve ölçüm tekniği önem taşır.{" "}
          <a href="https://pubmed.ncbi.nlm.nih.gov/718832/">
            Jackson ve Pollock, erkekler
          </a>
          ,{" "}
          <a href="https://pubmed.ncbi.nlm.nih.gov/7402053/">
            Jackson ve arkadaşları, kadınlar
          </a>
        </p>
        <h3>BMI / Deurenberg: Ölçüm ekipmanı olmadan tahmin</h3>
        <p>
          BMI temelli yöntemde mezura veya kaliper gerekmez; boyunu ve kilonu
          biliyor olman yeterlidir. Yaş ve cinsiyet bilgileriyle BMI değeri bir
          yağ yüzdesi tahminine dönüştürülür. Kas ile yağ doğrudan ölçülmediği
          için sonucu ayrıntılı bir vücut analizi olarak değerlendirmemelisin.{" "}
          <a href="https://research.vu.nl/en/publications/body-mass-index-as-a-measure-of-body-fatness-age-and-sex-specific/">
            Deurenberg ve arkadaşları, 1991
          </a>
        </p>
        <h2 id="mezura-olcumu">
          Evde mezurayla bel, boyun ve kalça ölçüsü nasıl alınır?
        </h2>
        <p>
          Esnemeyen bir mezura kullan. Dik ama rahat dur; karnını içeri çekme.
          Mezura cilde temas etsin, fakat dokuyu sıkıştırmasın. Gevşek bırakıp
          arada boşluk oluşturmak da ölçüyü değiştirir.
        </p>
        <p>Navy yöntemi için temel ölçüm noktaları şunlardır:</p>
        <ol>
          <li>
            <strong>Boy:</strong> Ayakkabısız ölç ve santimetre olarak kaydet.
          </li>
          <li>
            <strong>Boyun:</strong> Karşıya bakarken, omuzların rahat
            durumdayken gırtlak çıkıntısının hemen altından ölç. Mezurayı
            çıkıntının üzerinden geçirme.
          </li>
          <li>
            <strong>Erkeklerde karın:</strong> Göbek deliği hizasından, mezurayı
            yere paralel tutarak ölç.
          </li>
          <li>
            <strong>Kadınlarda bel:</strong> Gövdenin doğal olarak en dar
            bölgesinden ölç.
          </li>
          <li>
            <strong>Kadınlarda kalça:</strong> Kalçanın en geniş çevresini,
            mezura yatay kalacak şekilde ölç.
          </li>
        </ol>
        <p>
          Karın ve bel ölçüsünü normal nefes verişinin sonunda al. Bu tarifler
          Navy çevre yöntemine aittir; başka bir bel çevresi protokolündeki
          ölçüm noktasını otomatik olarak buraya taşımamalısın.{" "}
          <a href="https://s3.us-east-2.amazonaws.com/sidearm.nextgen.sites/usmmasports.com/documents/2025/2/24/Navy_BCA_guide.pdf">
            Navy ölçüm rehberi
          </a>
        </p>
        <h3>Ölçerken en sık karıştırılan iki şey</h3>
        <p>
          <strong>
            Belin en dar noktası ile göbek deliği hizası aynı olmayabilir.
          </strong>{" "}
          Erkek Navy hesabında “bel” alanını doldururken göbek deliği
          hizasındaki karın çevresini esas al. Kadın hesabında doğal bel
          ölçüsünü kullan.
        </p>
        <p>
          <strong>Santimetre ile milimetre farklı girdilerdir.</strong> Navy
          çevreleri santimetre, kaliper ölçümleri milimetre olarak girilir.
          Kaliperle ölçtüğün 12 mm değerini 1,2 şeklinde girmek sonucu
          değiştirir.
        </p>
        <p>
          Pratik bir kontrol olarak ölçüm setini yeniden al. Belirgin fark varsa
          mezuranın yerini ve gerginliğini kontrol et; küçük çıkan değeri
          özellikle seçme. Takip için hangi noktayı kullandığını not etmek, bir
          sonraki ölçümün karşılaştırılabilir olmasını sağlar.
        </p>
        <h2 id="formuller">
          Vücut yağ oranı hesaplama formülü ve örnek sonuçlar
        </h2>
        <p>
          AtalayTheCoach hesaplayıcısında kullanılan denklemler aşağıdadır.
          Bütün uzunluklar <strong>santimetre</strong>,{" "}
          <span>
            log<sub>10</sub>
          </span>{" "}
          ise 10 tabanlı logaritmadır. Bu denklemler vücut çevrelerinden yağ
          yüzdesi tahmini üretir.{" "}
          <a href="https://smj.sma.org.sg/5306/5306a7.pdf">
            Formüllerin kullanıldığı araştırma
          </a>
        </p>
        <p>
          <strong>Erkekler için:</strong>
        </p>
        <BodyFatFormula name="male" />
        <p>
          <strong>Kadınlar için:</strong>
        </p>
        <BodyFatFormula name="female" />
        <p>
          Formüllerdeki noktalar ondalık ayırıcıdır. İnç cinsinden ölçüm isteyen
          başka bir denklemde aynı katsayıları dönüşüm yapmadan kullanamazsın.
        </p>
        <h3>Erkek ve kadın için örnek hesaplama</h3>
        <p>
          Aşağıdaki değerler gerçek kişilerin ölçüm sonuçları değil, formülün
          işleyişini gösteren hesaplanmış örneklerdir.
        </p>
        <div className="overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th scope="col">Girdi</th>
                <th scope="col">Erkek örneği</th>
                <th scope="col">Kadın örneği</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Boy</td>
                <td>180 cm</td>
                <td>165 cm</td>
              </tr>
              <tr>
                <td>Boyun çevresi</td>
                <td>38 cm</td>
                <td>33 cm</td>
              </tr>
              <tr>
                <td>Karın / doğal bel çevresi</td>
                <td>90 cm</td>
                <td>75 cm</td>
              </tr>
              <tr>
                <td>Kalça çevresi</td>
                <td>Kullanılmaz</td>
                <td>100 cm</td>
              </tr>
              <tr>
                <td>Hesaplanan yağ oranı</td>
                <td>Yaklaşık %19,8</td>
                <td>Yaklaşık %29,4</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          Erkek örneğinde diğer ölçüleri sabit tutup karın çevresini 92 cm
          girdiğimizde sonuç yaklaşık <strong>%21,2</strong> olur. İki
          santimetrelik giriş farkı bu örnekte yaklaşık{" "}
          <strong>1,4 yüzde puanlık</strong> fark oluşturur. Bu, yöntemin genel
          hata payı değildir; ölçüm girdisindeki değişimin aynı formülde
          oluşturduğu farktır.
        </p>
        <h3>BMI temelli yağ oranı nasıl bulunur?</h3>
        <p>Yetişkinler için kullanılan Deurenberg denklemi şöyledir:</p>
        <BodyFatFormula name="bmi" />
        <p>
          Bu yöntem, Navy’den farklı bilgi kullanır. Aynı kişide iki yöntemin
          aynı sonucu vermesi beklenmez. Orijinal çalışmada yetişkin denklemi
          için bildirilen tahminin standart hatası 4,1 yağ yüzdesi puanıdır; bu
          sayı her birey için garantili bir ±4,1 aralık anlamına gelmez.{" "}
          <a href="https://research.vu.nl/en/publications/body-mass-index-as-a-measure-of-body-fatness-age-and-sex-specific/">
            Deurenberg ve arkadaşları
          </a>
        </p>
        <h2 id="sonucu-yorumlama">
          Kadınlarda ve erkeklerde yağ oranı kaç olmalı?
        </h2>
        <p>
          Tek bir yüzdeyi herkes için ideal hedef olarak seçmek doğru bir
          yaklaşım değildir. Yaş, cinsiyet, ölçüm yöntemi ve referans alınan
          topluluk, tabloların yorumunu değiştirir. Sık paylaşılan yaş
          tablolarının dayanaklarından biri, yağ yüzdesini BMI sınırlarıyla
          ilişkilendiren Gallagher ve arkadaşlarının çalışmasıdır.
        </p>
        <div className="overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th scope="col">Yaş</th>
                <th scope="col">Erkekler için referans aralığı</th>
                <th scope="col">Kadınlar için referans aralığı</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>20–39</td>
                <td>%8 ile %20’nin altı</td>
                <td>%21 ile %33’ün altı</td>
              </tr>
              <tr>
                <td>40–59</td>
                <td>%11 ile %22’nin altı</td>
                <td>%23 ile %34’ün altı</td>
              </tr>
              <tr>
                <td>60–79</td>
                <td>%13 ile %25’in altı</td>
                <td>%24 ile %36’nın altı</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          Bu aralıklar araştırmanın Tablo 4’ündeki eşiklerden aktarılmıştır.
          Araştırmacılar bunları kesin kişisel sağlık sınırları olarak sunmaz.
          Türkiye’ye özgü normlar değildir ve Navy sonucuna uygulandığında ek
          belirsizlik taşır.{" "}
          <a href="https://zakboekdietetiek.nl/wp-content/uploads/2015/11/Am-J-Clin-Nutr-2000-Gallagher-694-701.pdf">
            Gallagher ve arkadaşları, 2000
          </a>
        </p>
        <h3>Referans aralığı, kişisel hedefin değildir</h3>
        <p>
          Tablolarda karşılaşabileceğin “sporcu”, “fitness” veya “ortalama” gibi
          referans etiketleri bir sınıflandırma sunar. Tek başlarına kişinin ne
          kadar güçlü olduğunu, iyi beslendiğini veya sağlıklı olduğunu
          kanıtlamazlar. Yağ oranı tablosunu bir yarışma sıralaması gibi okumak
          yerine, ölçümün bağlamını dikkate al.
        </p>
        <p>
          Örneğin aynı kişi farklı yöntemlerde farklı kategorilere düşebilir.
          Sınırdaki bir sonucu gördüğünde ilk adımın beslenmeni aniden
          değiştirmek değil, girdileri ve ölçüm yöntemini kontrol etmek olsun.
          Sağlık açısından yorum gerektiğinde klinik değerlendirme, tek
          hesaplayıcı sonucundan daha kapsamlı bilgi kullanır.{" "}
          <a href="https://www.cdc.gov/bmi/faq/">
            CDC: BMI ve vücut yağı değerlendirmesi
          </a>
        </p>
        <h2 id="arastirmalar">
          Yağ oranı hesaplama ne kadar güvenilir? Türkiye’deki çalışmalar
        </h2>
        <p>
          Bir yöntemin pratik olması, herkes için aynı doğrulukta sonuç verdiği
          anlamına gelmez. Türkiye’de yapılan araştırmalar da ölçüm yöntemini ve
          kullanılan denklemi dikkate almamız gerektiğini gösteriyor.
        </p>
        <p>
          <strong>208 üniversite öğrencisinde yöntem karşılaştırması:</strong>{" "}
          Düz ve arkadaşlarının çalışmasında deri kıvrımı, ultrason ve BIA
          sonuçları DXA ile karşılaştırıldı. Araştırmacılar, bu örneklemde
          yöntemlerin birbirinin yerine kullanılamayacağını bildirdi. Takip
          açısından anlamı açık: başlangıçta bir yöntem, sonra başka bir yöntem
          kullanırsan farkın ne kadarının vücut değişimi olduğunu ayırt etmek
          zorlaşır.{" "}
          <a href="https://open.metu.edu.tr/handle/11511/48046">
            Düz ve arkadaşları, 2009
          </a>
        </p>
        <p>
          <strong>103 yetişkinde antropometrik denklemler:</strong> Kılınç ve
          Köksal, farklı denklemlerle tahmin edilen vücut bileşimini BIA
          sonuçlarıyla karşılaştırdı. Yöntemler arasında farklılıklar saptandı.
          Bu çalışma, ölçümlerden vücut bileşimi tahminini ele alır; doğrudan
          Navy hesaplayıcısının DXA karşısındaki doğruluğunu test etmez.{" "}
          <a href="https://dergipark.org.tr/en/pub/ssd/article/1179301">
            Kılınç ve Köksal, 2023
          </a>
        </p>
        <p>
          <strong>195 genç yetişkinde sekiz BIA denklemi:</strong> Acar ve
          arkadaşları, yağsız kütle tahminlerini DXA ile karşılaştırdı. Sonuçlar
          denkleme ve cinsiyete göre değişti; denklemlerin uygulandıkları
          topluluğa özgü olabileceği vurgulandı. BIA cihazlarının verdiği
          sonuçların da kullanılan modelden bağımsız olmadığını görmek açısından
          bu bulgu önemlidir.{" "}
          <a href="https://dergipark.org.tr/tr/download/article-file/4289756">
            Acar ve arkadaşları, 2024
          </a>
        </p>
        <p>
          Bu araştırmalardan “Türkiye’de herkes için en doğru yöntem budur”
          sonucu çıkmaz. Bulguların uygulamadaki karşılığı, yöntemi kaydetmek ve
          ölçümleri tutarlı koşullarda karşılaştırmaktır.
        </p>
        <h3>Gelişimini nasıl takip edebilirsin?</h3>
        <p>
          Her kayıtta tarih, yöntem, ham ölçüler, kilo ve hesaplanan yüzdeyi
          birlikte sakla. BIA kullanıyorsan sıvı durumunun ölçümü
          etkileyebileceğini ve cihazın hazırlık talimatlarına uyman gerektiğini
          unutma. Türkiye’deki 2024 çalışmasında da hidrasyon ve ölçüm koşulları
          kontrol edilmiştir.{" "}
          <a href="https://dergipark.org.tr/tr/download/article-file/4289756">
            Acar ve arkadaşları
          </a>
        </p>
        <p>
          Tek ölçümdeki küçük değişime dayanarak “kas kaybettim” veya
          “yağlandım” sonucuna varma. Birkaç ölçümün yönünü; bel çevresi, kilo
          ve antrenman kayıtlarınla birlikte değerlendir. En düşük sonucu veren
          yöntemi seçmek yerine, doğru uygulayabildiğin yöntemde tutarlılık ara.
        </p>
        <p>
          Yağ oranı %25’ten %20’ye düştüğünde değişim{" "}
          <strong>5 yüzde puandır</strong>. Bu, doğrudan 5 kg yağ kaybı demek
          değildir. Örneğin başlangıçta 80 kg ve %25 yağ oranı 20 kg yağ
          kütlesine; sonrasında 75 kg ve %20 ise 15 kg yağ kütlesine karşılık
          gelir. Bu varsayımsal örnekte fark 5 kg’dır; başka kilolarda farklı
          çıkar.
        </p>
        <p>
          Sonraki adımda{" "}
          <a href="/essay/calori-calculator">kalori ve TDEE hesaplayıcısını</a>{" "}
          inceleyebilirsin. Yağsız kütleyi boya göre değerlendiren{" "}
          <a href="/essay/ffmi-calculator">FFMI hesaplayıcısını</a> kullanırken
          de başlangıçtaki yağ yüzdesi tahmininin belirsizliğini aklında tut.
        </p>
        <h3>Kaynakça</h3>
        <ol>
          <li>
            <a href="https://s3.us-east-2.amazonaws.com/sidearm.nextgen.sites/usmmasports.com/documents/2025/2/24/Navy_BCA_guide.pdf">
              Navy, Body Composition Assessment Guide, Ocak 2023
            </a>
          </li>
          <li>
            <a href="https://smj.sma.org.sg/5306/5306a7.pdf">
              Goonasegaran ve arkadaşları, 2012 — BMI ve yağ yüzdesi
              karşılaştırması
            </a>
          </li>
          <li>
            <a href="https://pubmed.ncbi.nlm.nih.gov/718832/">
              Jackson ve Pollock, 1978 — Erkeklerde vücut yoğunluğu denklemleri
            </a>
          </li>
          <li>
            <a href="https://pubmed.ncbi.nlm.nih.gov/7402053/">
              Jackson ve arkadaşları, 1980 — Kadınlarda vücut yoğunluğu
              denklemleri
            </a>
          </li>
          <li>
            <a href="https://research.vu.nl/en/publications/body-mass-index-as-a-measure-of-body-fatness-age-and-sex-specific/">
              Deurenberg ve arkadaşları, 1991 — BMI temelli yağ yüzdesi tahmini
            </a>
          </li>
          <li>
            <a href="https://zakboekdietetiek.nl/wp-content/uploads/2015/11/Am-J-Clin-Nutr-2000-Gallagher-694-701.pdf">
              Gallagher ve arkadaşları, 2000 — BMI temelli referans
              aralıklarının geliştirilmesi
            </a>
          </li>
          <li>
            <a href="https://open.metu.edu.tr/handle/11511/48046">
              Düz ve arkadaşları, 2009 — Türkiye örnekleminde yöntem
              karşılaştırması
            </a>
          </li>
          <li>
            <a href="https://dergipark.org.tr/en/pub/ssd/article/1179301">
              Kılınç ve Köksal, 2023 — Antropometrik denklemler ve BIA uyumu
            </a>
          </li>
          <li>
            <a href="https://dergipark.org.tr/tr/download/article-file/4289756">
              Acar ve arkadaşları, 2024 — Genç yetişkinlerde BIA denklemlerinin
              geçerliği
            </a>
          </li>
          <li>
            <a href="https://www.cdc.gov/bmi/faq/">
              CDC — BMI hakkında sık sorulan sorular
            </a>
          </li>
          <li>
            <a href="https://pmc.ncbi.nlm.nih.gov/articles/PMC2891061/">
              Nevill ve arkadaşları — Jackson–Pollock denklemlerinin sınırları
            </a>
          </li>
        </ol>
      </article>
    </section>
  );
}
